import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { useParams } from 'react-router-dom';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import Checkbox from '@atlaskit/checkbox';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import {
  NotificationTypes,
  WarningDialog,
  successNotificationBuilder,
  useApplicationContext,
  useFeatureToggle,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { ROVO_PRODUCT_ID, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import {
  usePricingPlanOptions,
  useProductLineItemsStore,
  useStore,
  useSelectedProductIdsWithoutChanges,
  useOrgProductInstancesMap,
} from '../../../store';
import { BulkChangeBillDatesModal } from './components/bulk-change-bill-dates-modal';
import { ProductLineItem } from './components/product-line-item';
import { ProductLineItemsTable } from './components/product-line-items-table';
import { ProductRowsContainer } from './components/product-rows-container';
import { BulkActionsSection } from './components/bulk-actions-section';
import { MatchBillDates } from './components/match-bill-dates';
import { SelectType } from '../../billing-profile/src/types';
import { CartProductSchema, CartLinkedEntitlementsSchema, OperationType } from '../../../types';
import { getIdsOfLinkedProductsAndApps } from '../../../store/utils';
import MatchBillDateSuccessFlag from './components/match-bill-date-success-flag';
import { MatchBillDatesWarningModal } from './components/match-bill-dates-warning-modal';
import './product-line-items-wrapper.scss';
import { CatalogueContextItems } from '../../add-new-products/src/types';
import { BulkUserChangeModal } from './components/bulk-user-change-modal';

export enum SIDEBAR {
  MATCH_BILL_DATE = 'matchBillDates',
}

const allowedSidebars = {
  [SIDEBAR.MATCH_BILL_DATE]: MatchBillDates,
};

const useSelectedPartnerAccountData = () => {
  const { txaId } = useParams<{ txaId: string }>();
  const userData = useApplicationContext();
  const selectedPartnerAccountData = useMemo(
    () =>
      userData?.partnerAccounts?.find(
        ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
      ),
    [txaId, userData?.partnerAccounts]
  );
  return selectedPartnerAccountData ?? {};
};

export const ProductLineItemsWrapper = ({
  emptyCart,
  isClearAllLineItems,
  updateIsClearAllLineItems,
  hasUnchangedLineItems,
  showUnchangedLineItems,
  currency,
  onAddNewProductsClick,
  handleAddNewAppsClick,
  setHasErrorFetchingOfferings,
}: {
  emptyCart: React.ReactNode;
  isClearAllLineItems?: boolean;
  updateIsClearAllLineItems?: (isClearAllLineItems: boolean) => void;
  hasUnchangedLineItems?: boolean;
  showUnchangedLineItems?: boolean;
  currency?: string;
  onAddNewProductsClick?: (cloudSiteId: string, cloudSiteHostName: string) => void;
  handleAddNewAppsClick?: (params: {
    product: any;
    cloudSiteId: string;
    cloudSiteUrl: string;
    onAddToCart: (OrderItems: CatalogueContextItems) => void;
    sandboxCloudSiteParentProductIds: string[];
  }) => void;
  setHasErrorFetchingOfferings?: (hasError: boolean) => void;
}) => {
  const [productLineItems, actions] = useProductLineItemsStore();
  const productIdPlanOptionsMap = usePricingPlanOptions();
  const [
    {
      hasProductMetadataFetchFailed,
      validationErrors,
      products,
      linkedEntitlements,
      selectedProducts,
      selectedLinkedEntitlements,
      hasNativeProductWithPricingPlanFetchFailed,
      nextBillDate,
      enrichmentStatus,
      featureFlags,
    },
  ] = useStore();
  const orgProductInstancesMap = useOrgProductInstancesMap();

  const { openSidebar, addToAllowlistSidebars, closeSidebar } = useSidebar();
  const {
    isOpen: isCoBillWarningModalOpen,
    open: openCoBillWarningModal,
    close: closeCoBillWarningModal,
  } = useOpenClose();

  const {
    isOpen: isChangeBillDatesModalOpen,
    open: openChangeBillDateModal,
    close: closeChangeBillDateModal,
  } = useOpenClose();

  const {
    isOpen: isChangeBulkUserModalOpen,
    open: openChangeBulkUserModal,
    close: closeChangeBulkUserModal,
  } = useOpenClose();

  const selectedPartnerAccountData = useSelectedPartnerAccountData();
  const { txaId } = useParams<{ txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  const showSandboxProducts = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS] || false;
  const isSandboxDecouplingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING] || false;
  const isNonCommercialPricingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING] || false;
  const isTWCPhase2Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2] || false;
  const isDesignIteration1Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1] || false;
  const isAppificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APPIFICATION);
  const isMultiCurrencyEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES] || false;
  const isAddOnsSearchEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2] ?? false;
  const [isRemoveItemConfirmationOpen, setIsRemoveItemConfirmationOpen] = useState(false);
  const removeLineItemsRef = useRef({ productId: '', cloudSite: '', checkedProductIds: [''] });
  const [checkedItemMap, setCheckedItemMap] = useState<Record<string, SelectType>>({});
  const [selectAll, setSelectAll] = useState(false);
  const [selectedEntitlementIdForBillDateChange, setSelectedEntitlementIdForBillDateChange] = useState<
    string | undefined
  >(undefined);
  const [isIndeterminate, setIsIndeterminate] = useState(true);
  const [onSuccessDateChange, setOnSuccessDateChange] = useState<{
    title?: string;
    description: string;
  } | null>(null);
  const selectedProductIdsWithoutChanges = useSelectedProductIdsWithoutChanges();
  const { sendSuccessNotification, setNotificationSent } = useNotification();
  // Below useEffect is setting Indeterminate state, i.e, when selected products or apps in cart are partially checked
  useEffect(() => {
    let isAllSelectedProductsChecked = false;
    let someSelectedProductsChecked = false;
    let isAllSelectedAppsChecked = false;
    let someSelectedAppsChecked = false;
    if (Object.keys(checkedItemMap).length > 0 && selectedProducts) {
      isAllSelectedProductsChecked = Object.keys(selectedProducts).every((productId) =>
        Object.keys(checkedItemMap).includes(productId)
      );
      someSelectedProductsChecked = Object.keys(selectedProducts).some((productId) =>
        Object.keys(checkedItemMap).includes(productId)
      );
    }
    if (Object.keys(checkedItemMap).length > 0 && selectedLinkedEntitlements && linkedEntitlements) {
      const allApps = Object.values(linkedEntitlements)?.flatMap((item) => item);
      const filteredSelectedApps = Object.fromEntries(
        Object.entries(selectedLinkedEntitlements).filter(
          ([key]) => allApps.find((app) => app.id === key)?.operationType === OperationType.NEW
        ) || {}
      );
      isAllSelectedAppsChecked = Object.keys(filteredSelectedApps).every((appId) =>
        Object.keys(checkedItemMap).includes(appId)
      );
      someSelectedAppsChecked = Object.keys(filteredSelectedApps).some((appId) =>
        Object.keys(checkedItemMap).includes(appId)
      );
    }
    setSelectAll(isAllSelectedProductsChecked && isAllSelectedAppsChecked);
    setIsIndeterminate(
      (someSelectedProductsChecked || someSelectedAppsChecked) &&
        !(isAllSelectedProductsChecked && isAllSelectedAppsChecked)
    );
  }, [checkedItemMap, selectedProducts, selectedLinkedEntitlements, linkedEntitlements]);

  // Below useEffect is clearing all the checked products in the cart,
  // whenever selectAll checkbox is toggled (either when prodcucts in cart are partially checked OR all the products in cart are checked)
  useEffect(() => {
    if (isIndeterminate) {
      return;
    }
    if (!selectAll) {
      setCheckedItemMap({});
    } else {
      Object.keys(selectedProducts).forEach((productId) => {
        checkedItemMap[productId] = SelectType.ENTITLEMENT;
        checkedItemMap[products[productId].cloudSite] = SelectType.SITE;

        const { isValid, orgName } = isOrgProduct(productId);
        if (isValid && orgName) {
          checkedItemMap[orgName] = SelectType.ORG;
        }

        const { linkedNewApps } = getAllLinkedNewApps(productId);
        const linkedNewAppsIds = Object.keys(linkedNewApps);
        linkedNewAppsIds.forEach((appId) => {
          checkedItemMap[appId] = SelectType.ENTITLEMENT;
        });
      });

      setCheckedItemMap({ ...checkedItemMap });
    }
  }, [selectAll]);

  // Below useEffect is looping through all checked items, and checking if checked is present in selected products.
  // If not present in selected product we are removing it from checked map.
  // If any one of the product in cloudsite is unChecked, avoiding removing cloudsite, or else remove cloudsite from checkedMap
  useEffect(() => {
    const copyOfCheckedItemMap = { ...checkedItemMap };
    Object.keys(checkedItemMap).map((id) => {
      if (
        !(
          Object.keys(selectedProducts).includes(id) ||
          (selectedLinkedEntitlements && Object.keys(selectedLinkedEntitlements).includes(id))
        ) &&
        checkedItemMap[id] !== SelectType.SITE &&
        checkedItemMap[id] !== SelectType.ORG
      ) {
        delete copyOfCheckedItemMap[id];
        const { isValid, cloudsite } = isAllProductInCloudsiteChecked(id, copyOfCheckedItemMap);
        if (!isValid) {
          delete copyOfCheckedItemMap[cloudsite];
        }

        const { isValid: isOrgProductValid, orgName } = isOrgProduct(id);
        if (isOrgProductValid && orgName) {
          delete copyOfCheckedItemMap[orgName];
        }
      }
    });
    setCheckedItemMap({ ...copyOfCheckedItemMap });
  }, [selectedProducts, selectedLinkedEntitlements]);

  // This below code is also setting BillToParty from selectedPartnerAccountData which is used in Order Summary screen
  useEffect(() => {
    actions.setSelectedPartnerAccountData(selectedPartnerAccountData);
  }, [actions, selectedPartnerAccountData]);

  useEffect(() => {
    if (isClearAllLineItems) {
      setIsRemoveItemConfirmationOpen(true);
    }
  }, [isClearAllLineItems]);

  useEffect(() => {
    addToAllowlistSidebars({
      ...allowedSidebars,
    });
  }, [addToAllowlistSidebars, closeSidebar, allowedSidebars]);

  const hasGenericPageLoadError = useMemo(() => {
    const { discountTier, transactionAccountId } = selectedPartnerAccountData;

    const isEmptyPartnerAccountData = !discountTier || !transactionAccountId;
    return isEmptyPartnerAccountData || hasNativeProductWithPricingPlanFetchFailed;
  }, [selectedPartnerAccountData, hasNativeProductWithPricingPlanFetchFailed]);

  const hasSubscriptionsWithSandboxForTransfer = selectedProducts
    ? Object.keys(selectedProducts).some((id) => {
        const product = products[id];
        return (
          product.isSandbox && product.productId !== ROVO_PRODUCT_ID && product.operationType === OperationType.TRANSFER
        );
      })
    : false;

  const genericPageLoadErrorContent = hasGenericPageLoadError ? (
    <SectionMessage
      appearance="warning"
      actions={[
        <SectionMessageAction onClick={() => window.location.reload()} key="reload">
          Refresh
        </SectionMessageAction>,
      ]}
      testId="top-level-generic-error-banner"
    >
      <p>Some error occurred during page load. Please refresh.</p>
    </SectionMessage>
  ) : null;

  const getAllLinkedNewApps = (parentEntitlementId: string) => {
    const linkedNewApps: Record<string, CartLinkedEntitlementsSchema> = {};
    const linkedProductsAndAppsIds = getIdsOfLinkedProductsAndApps({
      productId: parentEntitlementId,
      productsObj: products,
      appsObj: linkedEntitlements,
    });
    if (linkedProductsAndAppsIds?.appIds?.length > 0) {
      linkedProductsAndAppsIds.appIds.forEach((appId) => {
        const allApps = Object.values(linkedEntitlements)?.flatMap((item) => item);
        const app = allApps.find((app) => app.id === appId && app.operationType === OperationType.NEW);
        if (app) {
          linkedNewApps[appId] = app;
        }
      });
    }
    return {
      linkedNewApps: linkedNewApps,
    };
  };

  const getContainerOrProductId = (appId: string) => {
    const allApps = Object.values(linkedEntitlements)?.flatMap((item) => item);
    const app = allApps.find((app) => app.id === appId);
    return app?.containerOrProductId || '';
  };

  let allProducts: CartProductSchema[] = [];
  if (products) {
    allProducts = Object.values(products);
  }
  let productsGroupedByCloudSite = groupBy(allProducts, 'cloudSite');

  /**
   * Filtering out the products which are not part of organisation product.
   */
  const filteredAllProducts = allProducts.filter((product) => !product.organisationName);
  productsGroupedByCloudSite = groupBy(filteredAllProducts, 'cloudSite');

  const isAllProductInCloudsiteChecked = (productOrAppId: string, updatedCheckedItem: Record<string, SelectType>) => {
    let isIdOfApp = false;
    let linkedNewAppsIds: string[] = [];
    let isAllSelectedAppsChecked = false;
    let cloudsiteOfParentProduct = '';
    // Checking if ID is of app
    if (selectedLinkedEntitlements && Object.keys(selectedLinkedEntitlements).includes(productOrAppId)) {
      isIdOfApp = true;
    }

    if (!isIdOfApp) {
      const { linkedNewApps } = getAllLinkedNewApps(productOrAppId);
      linkedNewAppsIds = Object.keys(linkedNewApps);
    } else {
      let parentEntitlementId = '';
      const containerOrProductId = getContainerOrProductId(productOrAppId);
      Object.values(products).forEach((product) => {
        if (product.id === containerOrProductId) {
          cloudsiteOfParentProduct = product.cloudSite;
          parentEntitlementId = product.id;
        }
        if (product.containerId === containerOrProductId) {
          cloudsiteOfParentProduct = product.cloudSite;
          parentEntitlementId = product.id;
        }
      });
      const { linkedNewApps } = getAllLinkedNewApps(parentEntitlementId);
      linkedNewAppsIds = Object.keys(linkedNewApps);
    }

    /**
     * This method is to get if all the selected products in cart within one cloudsite are checked.
     * useful for marking the cloudsite checked, when all the products withing that clodsite is checked.
     */
    const cloudsite = products[productOrAppId]?.cloudSite || cloudsiteOfParentProduct;
    const productsInCloudsite = productsGroupedByCloudSite[cloudsite];
    const selectedProductFromCloudsite = productsInCloudsite?.filter((product) =>
      Object.keys(selectedProducts).includes(product.id)
    );

    let isAllSelectedProductsChecked = false;
    if (Object.keys(updatedCheckedItem).length > 0 && selectedProductFromCloudsite?.length > 0) {
      isAllSelectedProductsChecked = selectedProductFromCloudsite?.every((product) =>
        Object.keys(updatedCheckedItem).includes(product.id)
      );
      isAllSelectedAppsChecked = linkedNewAppsIds?.every((appId) => Object.keys(updatedCheckedItem).includes(appId));
    }
    return { isValid: isAllSelectedProductsChecked && isAllSelectedAppsChecked, cloudsite: cloudsite };
  };

  const isOrgProduct = (productId: string) => {
    const orgName = products[productId]?.organisationName;
    return {
      isValid: !!(orgName && !isEmpty(orgName)),
      orgName: orgName,
    };
  };

  const handleCheckedProductLineItems = (checkedLineItemId: string) => {
    /**
     * This method is used to handle checking/unchecking checkbox in entitlement level.
     *
     * On checking the checkbox, this update the checkedItemMap state, with entitlementId.
     * at the same time, it check if all the products within a cloudsite checked, in this
     * case update the cloudsite info too in state checkedItemMap.
     *
     * On un-checking the checkbox, this delete the entitlement from state checkedItemMap, also
     * delete the cloudsite info from the state.
     */
    if (checkedLineItemId) {
      const checkedProductIds = Object.keys(checkedItemMap);
      const indexOfCheckedProduct = checkedProductIds.indexOf(checkedLineItemId);
      if (indexOfCheckedProduct >= 0) {
        const copyOfCheckedItemMap = { ...checkedItemMap };
        delete copyOfCheckedItemMap[checkedLineItemId];
        const { isValid, cloudsite } = isAllProductInCloudsiteChecked(checkedLineItemId, copyOfCheckedItemMap);
        if (!isValid) {
          delete copyOfCheckedItemMap[cloudsite];
        }
        const { isValid: isOrgProductValid, orgName } = isOrgProduct(checkedLineItemId);
        if (isOrgProductValid && orgName) {
          delete copyOfCheckedItemMap[orgName];
        }

        setCheckedItemMap({ ...copyOfCheckedItemMap });
      } else {
        const copyOfCheckedItemMap = { ...checkedItemMap };
        copyOfCheckedItemMap[checkedLineItemId] = SelectType.ENTITLEMENT;
        const { isValid, cloudsite } = isAllProductInCloudsiteChecked(checkedLineItemId, copyOfCheckedItemMap);
        if (isValid) {
          copyOfCheckedItemMap[cloudsite] = SelectType.SITE;
        }
        const { isValid: isOrgProductValid, orgName } = isOrgProduct(checkedLineItemId);
        if (isOrgProductValid && orgName) {
          copyOfCheckedItemMap[orgName] = SelectType.ORG;
        }

        setCheckedItemMap({ ...copyOfCheckedItemMap });
      }
    }
  };

  const handleCheckedCloudsites = (checkedCloudsite: string) => {
    /**
     * This method is used to handle checking/unchecking checkbox in cloudsite level.
     *
     * On checking the checkbox, this update the checkedItemMap state, with cloudsite name.
     * after which it loop through all the selected product (entitlements) within checked cloudsite,
     * and update the state with products.
     *
     * On un-checking the checkbox, it delete the cloudsite name from state checkedItemMap,
     * after which loop through all the products within the cloudsite and delete from state.
     */
    const checkedCloudsiteIdsProductIds = Object.keys(checkedItemMap);
    const indexOfCheckedCloudsite = checkedCloudsiteIdsProductIds.indexOf(checkedCloudsite);
    const productsInCloudSite = productsGroupedByCloudSite[checkedCloudsite];
    const productIdsInCloudSite = productsInCloudSite?.map((product) => product.id);
    if (indexOfCheckedCloudsite >= 0) {
      const copyOfCheckedItemMap = { ...checkedItemMap };
      delete copyOfCheckedItemMap[checkedCloudsite];
      productIdsInCloudSite?.forEach((productId) => {
        const indexOfProduct = checkedCloudsiteIdsProductIds.indexOf(productId);
        if (indexOfProduct >= 0) {
          delete copyOfCheckedItemMap[productId];
        }
        const { linkedNewApps } = getAllLinkedNewApps(productId);
        const linkedNewAppsIds = Object.keys(linkedNewApps);
        linkedNewAppsIds.forEach((appId) => {
          const indexOfApp = checkedCloudsiteIdsProductIds.indexOf(appId);
          if (indexOfApp >= 0) {
            delete copyOfCheckedItemMap[appId];
          }
        });
      });
      setCheckedItemMap({ ...copyOfCheckedItemMap });
    } else {
      const copyOfCheckedItemMap = { ...checkedItemMap };
      copyOfCheckedItemMap[checkedCloudsite] = SelectType.SITE;
      productIdsInCloudSite?.forEach((productId) => {
        if (Object.keys(selectedProducts).indexOf(productId) >= 0) {
          copyOfCheckedItemMap[productId] = SelectType.ENTITLEMENT;
        }
        const { linkedNewApps } = getAllLinkedNewApps(productId);
        const linkedNewAppsIds = Object.keys(linkedNewApps);
        linkedNewAppsIds.forEach((appId) => {
          if (selectedLinkedEntitlements && Object.keys(selectedLinkedEntitlements).indexOf(appId) >= 0) {
            copyOfCheckedItemMap[appId] = SelectType.ENTITLEMENT;
          }
        });
      });
      setCheckedItemMap({ ...copyOfCheckedItemMap });
    }
  };

  const filteredOrgProducts = allProducts.filter((product) => product.organisationName);
  const productsGroupedByOrganisation = groupBy(filteredOrgProducts, 'organisationName');

  const handleCheckedOrganisation = (checkedOrganisation: string) => {
    /**
     * This method is used to handle checking/unchecking checkbox in organisation level.
     * On checking the checkbox, this update the checkedItemMap state, with organisation name.
     * after which it loop through all the selected product (entitlements) within checked organisation,
     * and update the state with products.
     */
    const checkedOrganisationIdsProductIds = Object.keys(checkedItemMap);
    const indexOfCheckedOrganisation = checkedOrganisationIdsProductIds.indexOf(checkedOrganisation);
    const productsInOrganisation = productsGroupedByOrganisation[checkedOrganisation];
    const productIdsInOrganisation = productsInOrganisation?.map((product) => product.id);
    if (indexOfCheckedOrganisation >= 0) {
      const copyOfCheckedItemMap = { ...checkedItemMap };
      delete copyOfCheckedItemMap[checkedOrganisation];
      productIdsInOrganisation?.forEach((productId) => {
        const indexOfProduct = checkedOrganisationIdsProductIds.indexOf(productId);
        if (indexOfProduct >= 0) {
          delete copyOfCheckedItemMap[productId];
        }
      });
      setCheckedItemMap({ ...copyOfCheckedItemMap });
    } else {
      const copyOfCheckedItemMap = { ...checkedItemMap };
      copyOfCheckedItemMap[checkedOrganisation] = SelectType.ORG;
      productIdsInOrganisation?.forEach((productId) => {
        if (Object.keys(selectedProducts).indexOf(productId) >= 0) {
          copyOfCheckedItemMap[productId] = SelectType.ENTITLEMENT;
        }
      });
      setCheckedItemMap({ ...copyOfCheckedItemMap });
    }
  };

  const handleCheckedSelectAll = () => {
    if (isIndeterminate) {
      setCheckedItemMap({});
      setSelectAll(false);
      setIsIndeterminate(false);
    } else if (selectAll) {
      setCheckedItemMap({});
      setSelectAll(false);
    } else {
      setSelectAll(true);
    }
  };

  const cloudsiteRemovalCheckbox = (cloudSite: string, orgName?: string) => (
    /**
     * This method is to render the checkbox for cloudsite and organisation level.
     * For the products, for which we get orgName and is not empty, we are considering it as organisation level.
     * For the rest of the products, we are considering it as cloudsite level.
     */
    <Checkbox
      isChecked={orgName && !isEmpty(orgName) ? !!checkedItemMap[orgName] : !!checkedItemMap[cloudSite]}
      testId={`select-product-checkbox-${orgName || cloudSite}`}
      onChange={() =>
        orgName && !isEmpty(orgName) ? handleCheckedOrganisation(orgName) : handleCheckedCloudsites(cloudSite)
      }
      label=""
    />
  );

  const getAllLinkedProductsAndAppsForCheckedItems = (selectedEntitlementId?: string) => {
    const linkedProducts: Record<string, CartProductSchema> = {};
    const linkedApps: Record<string, CartLinkedEntitlementsSchema> = {};
    const allCheckedEntitlements = selectedEntitlementId
      ? [selectedEntitlementId]
      : Object.keys(checkedItemMap).filter((id) => checkedItemMap[id] !== SelectType.SITE);
    allCheckedEntitlements.forEach((checkedItemId) => {
      const linkedProductsAndAppsIds = getIdsOfLinkedProductsAndApps({
        productId: checkedItemId,
        productsObj: products,
        appsObj: linkedEntitlements,
      });
      if (linkedProductsAndAppsIds.productIds?.length > 0) {
        linkedProductsAndAppsIds.productIds.forEach((productId) => {
          if (!checkedItemMap[productId]) {
            linkedProducts[productId] = products[productId];
          }
        });
      }
      if (linkedProductsAndAppsIds.appIds?.length > 0) {
        linkedProductsAndAppsIds.appIds.forEach((appId) => {
          const allApps = Object.values(linkedEntitlements)?.flatMap((item) => item);
          const app = allApps.find((app) => app.id === appId);
          if (app) {
            linkedApps[appId] = app;
          }
        });
      }
    });

    return {
      linkedProducts: linkedProducts,
      linkedApps: linkedApps,
    };
  };

  const handleOpeningSidebar = (selectedEntitlementId?: string) => {
    const { linkedProducts, linkedApps } = getAllLinkedProductsAndAppsForCheckedItems(selectedEntitlementId);
    openSidebar({
      sidebarType: SIDEBAR.MATCH_BILL_DATE,
      payload: {
        contentString: {
          HEADING: 'Change bill date',
          SUBMIT_BUTTON: 'Confirm',
          NOTIFICATION_SUCCESS_HEADING: 'New billing date added',
        },
        transactionAccoundId: txaId,
        closeSidebar,
        checkedItemMap: selectedEntitlementId ? { [selectedEntitlementId]: SelectType.ENTITLEMENT } : checkedItemMap,
        linkedProducts,
        linkedApps,
        setOnSuccessDateChange,
      },
      sidebarOptions: {
        sidebarWidth: '600px',
        isOverlay: true,
      },
    });
  };

  const handleMatchBillDates = (selectedEntitlementId?: string) => {
    const linkedProductsAndApps = getAllLinkedProductsAndAppsForCheckedItems(selectedEntitlementId);
    if (
      Object.keys(linkedProductsAndApps.linkedApps).length > 0 ||
      Object.keys(linkedProductsAndApps.linkedProducts).length > 0
    ) {
      openCoBillWarningModal();
    } else {
      handleOpeningSidebar(selectedEntitlementId);
    }
  };

  const isNextBillDateForEntitlementAvailable = () => {
    if (nextBillDate.entitlementLevel) {
      return Object.keys(nextBillDate.entitlementLevel).length > 0;
    } else {
      return false;
    }
  };

  const isAllNewAppsParentChecked = useMemo(() => {
    // This is to disable the match bill date option. When only apps are checked.

    if (!linkedEntitlements) return true;

    const allSelectedApps = Object.values(linkedEntitlements)?.flatMap((item) => item);
    const allSelectedNewApps = allSelectedApps?.filter((app) => app.operationType === OperationType.NEW);

    const checkedEntitlementIds = Object.keys(checkedItemMap)?.filter(
      (entitlementId) => checkedItemMap[entitlementId] !== SelectType.SITE
    );

    const allCheckedNewApps: string[] = [];
    allSelectedNewApps?.forEach((newApp) => {
      if (checkedEntitlementIds.includes(newApp.id)) {
        allCheckedNewApps.push(newApp.id);
      }
    });

    const newAppsWithParentChecked: string[] = [];
    allSelectedNewApps?.forEach((newApp) => {
      if (checkedEntitlementIds.includes(newApp.id)) {
        const containerOrProductId = newApp.containerOrProductId;
        const linkedProductId =
          Object.values(products).find((product) => product.id === containerOrProductId)?.id || '';
        if (checkedEntitlementIds.includes(linkedProductId)) {
          newAppsWithParentChecked.push(newApp.id);
        }
        const allContainerProductSelected = Object.values(products).filter(
          (product) => product.containerId === containerOrProductId
        );
        if (allContainerProductSelected.some((product) => checkedEntitlementIds.includes(product.id))) {
          newAppsWithParentChecked.push(newApp.id);
        }
      }
    });

    return newAppsWithParentChecked.length === allCheckedNewApps.length;
  }, [checkedItemMap, linkedEntitlements, products]);

  const productLineItemsContent = productLineItems.map(
    ({ cloudSite, products, cloudSiteId, orgName, isOrgProduct }) => {
      const isExpandedForLineItem = !!(
        hasUnchangedLineItems &&
        showUnchangedLineItems &&
        products.some((product) => product.id && selectedProductIdsWithoutChanges.includes(product.id))
      );

      return (
        <ProductLineItem
          cloudSite={cloudSite}
          cloudSiteId={cloudSiteId}
          orgName={orgName}
          onRemoveCloudSiteClick={(cloudSite: string) => {
            setIsRemoveItemConfirmationOpen(true);
            removeLineItemsRef.current.cloudSite = cloudSite;
          }}
          isNetNewCatalogSelectionEnabled={true}
          selectiveCloudsiteRemovalEnable={true}
          isUpdatingNextBillDateDisabled={true}
          isV2DesignEnabled={true}
          key={cloudSite}
          isExpandedForLineItem={isExpandedForLineItem}
        >
          <ProductRowsContainer
            products={products}
            productIdPlanOptionsMap={productIdPlanOptionsMap}
            enrichmentStatus={enrichmentStatus}
            setSelectedPricingPlanId={(updatedPricingPlanId, updatedOfferingId, productId) => {
              actions.setSelectedPricingPlanId(productId, updatedPricingPlanId, updatedOfferingId);
            }}
            setActiveUsers={(updatedActiveUsers, productId) => {
              actions.setActiveUsers(productId, updatedActiveUsers);
            }}
            onRemoveProductFromCart={(productId: CartProductSchema['id']) => {
              removeLineItemsRef.current.productId = productId;
              setIsRemoveItemConfirmationOpen(true);
            }}
            cloudsiteRemovalCheckbox={cloudsiteRemovalCheckbox(cloudSite, orgName)}
            handleCheckedProductLineItems={handleCheckedProductLineItems}
            checkedItemMap={checkedItemMap}
            selectiveProductRemovalEnable={true}
            matchBillDateEnabled={true}
            selectedBillDate={nextBillDate?.cloudSiteLevel?.[cloudSite] || nextBillDate?.cartLevel}
            checkedBillDate={isNextBillDateForEntitlementAvailable() ? nextBillDate.entitlementLevel : {}}
            isStickyColumnEnabled={true}
            showProductsPromotionView={true}
            showTotalColumn={true}
            showTaxEstimationColumn={true}
            shouldShowTrialStatus={true}
            isAllowedToEditUsersCount={true}
            showUnchangedLineItems={showUnchangedLineItems}
            selectedProductIdsWithoutChanges={selectedProductIdsWithoutChanges}
            hideCustomerContactColumn={true}
            isOrgProduct={isOrgProduct}
            showSandboxProducts={showSandboxProducts}
            isSandboxDecouplingEnabled={isSandboxDecouplingEnabled}
            isNonCommercialPricingEnabled={isNonCommercialPricingEnabled}
          />
        </ProductLineItem>
      );
    }
  );

  const validationErrorSummary = validationErrors
    ? Object.keys(validationErrors).map((entitlementId) => {
        const productWithError = products[entitlementId];
        const validationError = validationErrors[entitlementId];

        return (
          <li key={entitlementId}>
            <strong>{productWithError.name} </strong>
            {productWithError.cloudSite ? `(${productWithError.cloudSite})` : ''}
            <ul>
              {validationError.errors.map(({ message }, index) => (
                <li key={index}>{message}</li>
              ))}
            </ul>
          </li>
        );
      })
    : null;

  const getCheckedEntitlementsList = () => {
    const checkedEntitlementsList = Object.keys(checkedItemMap).filter(
      (entitlementId) => checkedItemMap[entitlementId] === SelectType.ENTITLEMENT
    );
    if (showSandboxProducts && !isSandboxDecouplingEnabled) {
      /**
       * Remove all sandbox products from the list of checked entitlements.
       * Only send parent sandbox product to remove from the cart.
       * sandbox products to be removed are handled in the store.
       */
      const checkedEntitlementsListWithoutSandbox: string[] = [];
      checkedEntitlementsList.forEach((entitlementId) => {
        const sandboxParentId = products[entitlementId]?.sandboxParent;

        if (sandboxParentId && !checkedEntitlementsList.includes(sandboxParentId)) {
          checkedEntitlementsListWithoutSandbox.push(sandboxParentId);
        }

        if (!(products[entitlementId]?.isSandbox && sandboxParentId)) {
          checkedEntitlementsListWithoutSandbox.push(entitlementId);
        }
      });

      return checkedEntitlementsListWithoutSandbox;
    }

    /**
     * Adding this below logic to remove all the instances of the org product from the cart,
     * when the org product is selected for removal.
     *
     * Please note we do not have to bulk edit nextBillDate of instances with org parent, as instances are Free.
     */
    if (isTWCPhase2Enabled) {
      const checkedEntitlementsListWithOrgInstances: string[] = [];
      // Iterating through entire checked list.
      checkedEntitlementsList.forEach((identifier) => {
        // If the identifier is already present in list, then no need to add it again.
        if (!checkedEntitlementsListWithOrgInstances.includes(identifier)) {
          checkedEntitlementsListWithOrgInstances.push(identifier);
        }

        // If the product is an org product, select all the instances of the product
        products[identifier]?.isOrgProduct &&
          orgProductInstancesMap[identifier]?.forEach((instance) => {
            if (!checkedEntitlementsListWithOrgInstances.includes(instance.id)) {
              checkedEntitlementsListWithOrgInstances.push(instance.id);
            }
          });

        // if the product is an instance of an org product, select the parent org product
        if (
          products[identifier]?.parentEntitlementId &&
          !checkedEntitlementsListWithOrgInstances.includes(products[identifier]?.parentEntitlementId ?? '')
        ) {
          checkedEntitlementsListWithOrgInstances.push(products[identifier]?.parentEntitlementId ?? '');
        }
      });
      return checkedEntitlementsListWithOrgInstances;
    }

    return checkedEntitlementsList;
  };

  const checkedEntitlementsListTotal = useMemo(() => {
    return getCheckedEntitlementsList().length;
  }, [getCheckedEntitlementsList]);

  const showRemoveLineItemSuccessMessage = () => {
    const notificationOptions = successNotificationBuilder()
      .withId('removed-cart-line-items')
      .withHeader('Selected items removed')
      .withDismissAfter(5000)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  };

  const onSubmitBulkUsersChange = (activeUsersNumber: string) => {
    const allCheckedEntitlements = Object.keys(checkedItemMap).filter((id) => checkedItemMap[id] !== SelectType.SITE);
    const productArr: CartProductSchema[] = Object.values(products);
    allCheckedEntitlements.forEach((entitlementId) => {
      const product = productArr.find((product) => product.id === entitlementId);
      if (product) actions.setActiveUsers(product.id, activeUsersNumber);
    });
    const notificationOptions = successNotificationBuilder()
      .withId('quantity-cart-line-items')
      .withHeader('Success')
      .withMessage('Quantity has been successfully changed for the subscriptions.')
      .withDismissAfter(5000)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeChangeBulkUserModal();
  };

  const handleOnConfirm = () => {
    if (removeLineItemsRef.current.productId) {
      actions.toggleSelectedItem(removeLineItemsRef.current.productId, SelectType.ENTITLEMENT, true);
      removeLineItemsRef.current.productId = '';
    } else if (removeLineItemsRef.current.cloudSite) {
      actions.removeCloudSiteFromCart(removeLineItemsRef.current.cloudSite, true);
      removeLineItemsRef.current.cloudSite = '';
    } else if (isClearAllLineItems) {
      productLineItems.forEach(({ cloudSite }) => {
        actions.removeCloudSiteFromCart(cloudSite, true);
      });
      updateIsClearAllLineItems?.(false);
    } else if (removeLineItemsRef.current.checkedProductIds.length > 0) {
      // This is to remove all the checked items from the cart.
      const allCheckedProductIds = removeLineItemsRef.current.checkedProductIds;
      const transferContainerProductIds: { [containerId: string]: string } = {};
      const otherProductIds: string[] = [];
      allCheckedProductIds.forEach((identifier) => {
        const containerId = products[identifier]?.containerId;
        const isTransfer = products[identifier]?.operationType === OperationType.TRANSFER;
        // If the product is transfer and containerId is present, then add it to transferContainerProductIds
        // this is to remove the container family by single identifier. No need to send all the products ids for same container
        if (isTransfer && containerId && !transferContainerProductIds[containerId]) {
          transferContainerProductIds[containerId] = identifier;
        }
        // If the product is transfer and containerId is not present, then add it to otherProductIds
        else if (!isTransfer || (isTransfer && !containerId)) {
          otherProductIds.push(identifier);
        }
      });
      // getting all the productIds which are part of transfer products
      const transferProductIds = Object.values(transferContainerProductIds) ?? [];
      actions.toggleCheckedItem([...otherProductIds, ...transferProductIds]);
      removeLineItemsRef.current.checkedProductIds = [];
      setCheckedItemMap({});
      setSelectAll(false);
      showRemoveLineItemSuccessMessage();
    }
    setIsRemoveItemConfirmationOpen(false);
  };

  const cartLineItemsTable = (
    <ProductLineItemsTable
      productLineItems={productLineItems}
      productIdPlanOptionsMap={productIdPlanOptionsMap}
      enrichmentStatus={enrichmentStatus}
      handleCheckedSelectAll={handleCheckedSelectAll}
      isIndeterminate={isIndeterminate}
      isSelectAllChecked={Object.keys(checkedItemMap).length > 0}
      handleCheckedOrganisation={handleCheckedOrganisation}
      handleCheckedCloudsites={handleCheckedCloudsites}
      setSelectedPricingPlanId={(updatedPricingPlanId, updatedOfferingId, productId) => {
        actions.setSelectedPricingPlanId(productId, updatedPricingPlanId, updatedOfferingId);
      }}
      setActiveUsers={(updatedActiveUsers, productId) => {
        actions.setActiveUsers(productId, updatedActiveUsers);
      }}
      onRemoveProductFromCart={(productId: CartProductSchema['id']) => {
        removeLineItemsRef.current.productId = productId;
        setIsRemoveItemConfirmationOpen(true);
      }}
      handleMatchBillDates={(selectedEntitlementId?: string) => {
        // set the selected entitlement id for change bill date
        setSelectedEntitlementIdForBillDateChange(selectedEntitlementId);
        openChangeBillDateModal();
      }}
      handleCheckedProductLineItems={handleCheckedProductLineItems}
      checkedItemMap={checkedItemMap}
      selectiveProductRemovalEnable={true}
      matchBillDateEnabled={true}
      nextBillDate={nextBillDate}
      checkedBillDate={isNextBillDateForEntitlementAvailable() ? nextBillDate.entitlementLevel : {}}
      isStickyColumnEnabled={true}
      showProductsPromotionView={true}
      showTotalColumn={true}
      showTaxEstimationColumn={true}
      shouldShowTrialStatus={true}
      isAllowedToEditUsersCount={true}
      showUnchangedLineItems={showUnchangedLineItems}
      selectedProductIdsWithoutChanges={selectedProductIdsWithoutChanges}
      hideCustomerContactColumn={true}
      showSandboxProducts={showSandboxProducts}
      isSandboxDecouplingEnabled={isSandboxDecouplingEnabled}
      isNonCommercialPricingEnabled={isNonCommercialPricingEnabled}
      currency={currency}
      onAddNewProductsClick={onAddNewProductsClick}
      handleAddNewAppsClick={handleAddNewAppsClick}
      isTWCPhase2Enabled={isTWCPhase2Enabled}
      isDesignIteration1Enabled={isDesignIteration1Enabled}
      isAppificationEnabled={isAppificationEnabled}
      setHasErrorFetchingOfferings={setHasErrorFetchingOfferings}
      isMultiCurrencyEnabled={isMultiCurrencyEnabled}
      isAddOnsSearchEnabled={isAddOnsSearchEnabled}
    />
  );

  const openBillDateChangeModal = () => {
    const { linkedProducts, linkedApps } = getAllLinkedProductsAndAppsForCheckedItems(
      selectedEntitlementIdForBillDateChange
    );
    return (
      <BulkChangeBillDatesModal
        closeModal={closeChangeBillDateModal}
        checkedItemMap={
          selectedEntitlementIdForBillDateChange
            ? { [selectedEntitlementIdForBillDateChange]: SelectType.ENTITLEMENT }
            : checkedItemMap
        }
        linkedProducts={linkedProducts}
        linkedApps={linkedApps}
        setOnSuccessDateChange={setOnSuccessDateChange}
      />
    );
  };

  return (
    <div
      className={classnames('product-line-items-wrapper flex flex-column', {
        'product-line-items-wrapper-row-gap': !isDesignIteration1Enabled,
        'product-line-items-wrapper-row-gap-v2': !!isDesignIteration1Enabled,
      })}
      data-testid="product-line-items-wrapper"
    >
      {genericPageLoadErrorContent}
      {!productLineItems.length ? (
        <>{emptyCart}</>
      ) : (
        <>
          {hasProductMetadataFetchFailed && (
            <SectionMessage
              appearance="warning"
              actions={[
                <SectionMessageAction onClick={actions.getBillEstimate} key="tryagain">
                  Try again
                </SectionMessageAction>,
              ]}
              testId="top-level-cart-error-banner"
            >
              <p>
                We're unable to add all your selections. Try again. If this keeps happening, contact Partner Service
                Desk.
              </p>
            </SectionMessage>
          )}
          {showSandboxProducts && !isSandboxDecouplingEnabled && hasSubscriptionsWithSandboxForTransfer && (
            <SectionMessage>
              <p>
                This site containes one or more subscription with sandboxes. Choosing a subscription with a sandbox will
                result in the linked sandbox being transferred as well.
              </p>
            </SectionMessage>
          )}
          {validationErrors && (
            <SectionMessage appearance="warning" testId="order-validation-summary">
              <div>
                Please correct these and retry
                <ul>{validationErrorSummary}</ul>
              </div>
            </SectionMessage>
          )}
          <WarningDialog
            isOpen={isRemoveItemConfirmationOpen}
            onClose={() => {
              setIsRemoveItemConfirmationOpen(false);
              if (isClearAllLineItems) {
                updateIsClearAllLineItems?.(false);
              }
            }}
            onConfirm={handleOnConfirm}
            header={isDesignIteration1Enabled ? 'Confirm removal from order?' : 'Remove all selected items?'}
            description={
              isDesignIteration1Enabled ? (
                <>
                  {
                    'You’re about to remove items from the order. This action cannot be undone. You will loose all the changes you have made to these subscriptions. Do you wish to continue?'
                  }
                </>
              ) : (
                <></>
              )
            }
            okLabel={'Confirm'}
            testId={'remove-product-line-item'}
          />

          {!isDesignIteration1Enabled || (!!isDesignIteration1Enabled && checkedEntitlementsListTotal > 0) ? (
            <BulkActionsSection
              numberOfItemsSelected={getCheckedEntitlementsList().length}
              removeProductsFromCart={() => {
                removeLineItemsRef.current.checkedProductIds = getCheckedEntitlementsList();
                setIsRemoveItemConfirmationOpen(true);
              }}
              handleCheckedSelectAll={handleCheckedSelectAll}
              isIndeterminate={isIndeterminate}
              isChecked={Object.keys(checkedItemMap).length > 0}
              handleMatchBillDates={() => {
                // setting here undefined will clear the selected entitlement id for bulk change bill date
                setSelectedEntitlementIdForBillDateChange(undefined);
                if (isDesignIteration1Enabled) {
                  openChangeBillDateModal();
                } else {
                  handleMatchBillDates();
                }
              }}
              isMatchBillDateBtnDisabled={!isAllNewAppsParentChecked}
              enableDesignIteration1Changes={isDesignIteration1Enabled}
              handleBulkUserChange={openChangeBulkUserModal}
              hideChangeUserButton={false}
            />
          ) : null}

          {hasUnchangedLineItems && showUnchangedLineItems && (
            <SectionMessage appearance="warning">
              <p>
                No changes have been applied to these line items. To place the order, update highlighted line item or
                remove them from the cart.
              </p>
            </SectionMessage>
          )}
          {isDesignIteration1Enabled ? cartLineItemsTable : productLineItemsContent}
        </>
      )}
      {isCoBillWarningModalOpen && (
        <MatchBillDatesWarningModal
          openSidebar={() => {
            closeCoBillWarningModal();
            handleOpeningSidebar(selectedEntitlementIdForBillDateChange);
          }}
          closeModal={closeCoBillWarningModal}
        />
      )}
      {isChangeBillDatesModalOpen && openBillDateChangeModal()}

      {isChangeBulkUserModalOpen && (
        <BulkUserChangeModal closeModal={closeChangeBulkUserModal} onSubmitBulkUsersChange={onSubmitBulkUsersChange} />
      )}

      <MatchBillDateSuccessFlag
        onSuccessDateChange={onSuccessDateChange}
        setOnSuccessDateChange={setOnSuccessDateChange}
      />
    </div>
  );
};
